import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { updateContact, fetchContactById } from "@/services/contact/contactService";
import { ContactWithRelations } from "@/types/contact.types";
import { ContactForm } from "@/components/contact/ContactForm";

export default function EditContact() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [contact, setContact] = useState<ContactWithRelations | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadContact = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        const data = await fetchContactById(id);
        setContact(data);
      } catch (err: any) {
        setError(err.message || "Failed to load contact");
        toast({
          title: "Error",
          description: "Failed to load contact details",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadContact();
  }, [id, toast]);

  const handleSubmit = async (values: any) => {
    if (!id) return;
    
    try {
      setIsSubmitting(true);
      
      // Convert date objects to ISO strings for database storage
      const formattedValues = {
        ...values,
        birthday: values.birthday ? new Date(values.birthday).toISOString() : null,
        last_contacted_date: values.last_contacted_date 
          ? new Date(values.last_contacted_date).toISOString() 
          : null,
        next_contact_date: values.next_contact_date
          ? new Date(values.next_contact_date).toISOString()
          : null,
      };
      
      await updateContact(id, formattedValues);
      
      toast({
        title: "Success",
        description: "Contact updated successfully",
      });
      
      // Navigate back to contact details page
      navigate(`/contacts/${id}`);
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to update contact",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="icon" asChild>
            <Link to={`/contacts/${id}`}>
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back to Contact</span>
            </Link>
          </Button>
          <h1 className="text-2xl font-bold">Edit Contact</h1>
        </div>
        <div className="rounded-md border p-8 text-center">
          <p>Loading contact details...</p>
        </div>
      </div>
    );
  }

  if (error || !contact) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/contacts">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back to Contacts</span>
            </Link>
          </Button>
          <h1 className="text-2xl font-bold">Edit Contact</h1>
        </div>
        <div className="rounded-md border border-destructive bg-destructive/10 p-8 text-center">
          <p className="text-destructive">{error || "Failed to load contact"}</p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => navigate(`/contacts/${id}`)}
          >
            Back to Contact
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2">
        <Button variant="ghost" size="icon" asChild>
          <Link to={`/contacts/${id}`}>
            <ArrowLeft className="h-4 w-4" />
            <span className="sr-only">Back to Contact</span>
          </Link>
        </Button>
        <h1 className="text-2xl font-bold">Edit {contact.first_name} {contact.last_name}</h1>
      </div>
      
      <ContactForm 
        initialValues={contact}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}